import { EndpointFilterRepository } from '../repository/EndpointFilterRepository';
import { GzEndpointFilterWebComponent } from '../component/GzEndpointFilterWebComponent';
import { EnumListEvents, EnumListItem, SelectedOption } from '../../gzEnumList/types';
import { ENDPOINT_LIST_ID, EndpointListEvents } from '../../gzEndpointList/types';
import { TargetEndpoint } from '@nirvana/shared/dtos/types/protected-entities/gz-console';
import { EndpointSpecialValues } from '@nirvana/shared/dtos/types/shared/filters';
import { isEmpty } from '@nirvana/shared/basic-types/object';

export class EndpointFilterController {
  private dependencies: any;
  private component: GzEndpointFilterWebComponent;
  private dataRepository: EndpointFilterRepository;
  private loadedEndpoints = 0;

  constructor(dependencies, component) {
    if (this.dependencies) {
      return;
    }

    this.dependencies = dependencies;
    this.buildDataRepository();
    this.component = component;
    this.addEventListeners();
  }

  addEventListeners(): void {
    this.component.addEventListener(EndpointListEvents.SEARCH_ENDPOINT, this.getEndpointsByName.bind(this));
    this.component.addEventListener(EndpointListEvents.SEARCH_CLEAR, this.onClearSearchBar.bind(this));
    this.component.addEventListener(EnumListEvents.SELECTION_CHANGE, this.handleSelectionChange.bind(this));
    this.component.addEventListener('open', this.handleDropdownOpen.bind(this));
    this.component.addEventListener('close', this.handleDropdownClose.bind(this));
  }

  private async getEndpointsByName(event: CustomEvent): Promise<void> {
    this.component.handleLoadingState(true);

    try {
      const data = await this.dataRepository.getEndpoints({
        companyId: this.component.companyId,
        endpointName: event.detail.searchString,
      });

      this.component.handleLoadingState(false);

      const additionalFilterOptions = this.removeDuplicateFilterOptions(
        this.formatTargetsToFilterOptions(data?.targets || [], [])
      );

      this.component.filterOptions = this.component.filterOptions.concat(additionalFilterOptions);
      await this.awaitListRerender();
      this.component.enumList.continueHandleOptionsSearch(event.detail.sourceEvent);
      this.loadedEndpoints = this.component.enumList.getNumberOfVisibleEndpoints();
    } catch (e: any) {
      this.component.handleLoadingState(false);
      this.component.isError = true;
    }
  }

  private formatTargetsToFilterOptions(targets: Array<TargetEndpoint>, selectedValues): EnumListItem[] {
    return targets.map(target => ({
      label: target.endpointName,
      value: target.endpointId,
      selected: selectedValues.length === 0 ? false : selectedValues.includes(target.endpointId),
    }));
  }

  private removeDuplicateFilterOptions(filterOptions: EnumListItem[]): EnumListItem[] {
    const existingEndpointIds = this.component
      .filterOptions
      .map((filterOption: EnumListItem): string => filterOption.value as string);

    return filterOptions.filter(
      (filterOption: EnumListItem): boolean => !existingEndpointIds.includes(filterOption.value as string)
    );
  }

  private async onClearSearchBar(event: CustomEvent): Promise<void> {
    await this.awaitListRerender();
    this.component.enumList.continueHandleOptionsSearch(event.detail.sourceEvent);
    this.loadedEndpoints = this.component.enumList.getNumberOfVisibleEndpoints();
  }

  private buildDataRepository(): void {
    this.dataRepository = new EndpointFilterRepository();
    this.dataRepository.injectDependencies(this.dependencies);
  }

  private async awaitListRerender(): Promise<void> {
    await this.component.updateComplete;
    await this.component.enumList.updateComplete;
  }

  private async handleSelectionChange(event: CustomEvent): Promise<void> {
    const selectedValues = event.detail.selectedValues;
    const selectedFilterOptions = this.component.filterOptions.filter(filterOption =>
      selectedValues.includes(filterOption.value)
    );
    const unselectedFilterOptions = this.component.filterOptions.filter(
      filterOption => !selectedValues.includes(filterOption.value)
    );

    selectedFilterOptions.forEach(selectedFilterOption => {
      selectedFilterOption.selected = true;
    });
    unselectedFilterOptions.forEach(selectedFilterOption => {
      selectedFilterOption.selected = false;
    });

    this.component.specialValueSelected =
      selectedFilterOptions.length === 0 || selectedValues.includes(EndpointSpecialValues.ALL);
    this.component.selectedFilterOptions = this.component.specialValueSelected
      ? this.getSpecialSelectedOption()
      : selectedFilterOptions;

    this.reorderEndpoints(selectedFilterOptions, unselectedFilterOptions);

    this.component.dispatchEvent(
      new CustomEvent(EndpointListEvents.ENDPOINT_SELECTION_CHANGED, {
        detail: { selectedFilterOptions: this.component.selectedFilterOptions },
        bubbles: true,
        composed: true,
      })
    );
  }

  private reorderEndpoints(selectedFilterOptions, unselectedFilterOptions) {
    unselectedFilterOptions.sort((a, b) => {
      const lowerA = a.label.toLowerCase();
      const lowerB = b.label.toLowerCase();
      if (lowerA === lowerB) return 0;
      return lowerA < lowerB ? -1 : 1;
    });

    this.component.filterOptions = selectedFilterOptions.concat(unselectedFilterOptions);
  }

  private async handleDropdownOpen(): Promise<void> {
    if (!this.component.enumList.shadowRoot) {
      return;
    }

    const endpointsList = this.component.enumList.shadowRoot.querySelector(`#${ENDPOINT_LIST_ID}`);

    if (!endpointsList) {
      return;
    }

    await this.awaitListRerender();
    endpointsList.scrollTop = 0;
  }

  private async handleDropdownClose(): Promise<void> {
    if (this.component.isError && this.loadedEndpoints !== 0) {
      this.component.isError = false;
    }
  }

  public async setFilterOptions(selectedValues): Promise<void> {
    try {
      await this.awaitListRerender();
      this.component.handleLoadingState(true);

      let defaultOptionsResponse;

      if (!this.component.targetEndpoints.has(this.component.companyId)) {
        defaultOptionsResponse = await this.dataRepository.getEndpoints({ companyId: this.component.companyId });

        this.component.targetEndpoints.set(this.component.companyId, defaultOptionsResponse);
      } else {
        defaultOptionsResponse = this.component.targetEndpoints.get(this.component.companyId);
      }

      const targets = defaultOptionsResponse?.targets || [];

      if (selectedValues.length && !selectedValues.includes(EndpointSpecialValues.ALL)) {
        const targetEndpointIds = targets.map((target: TargetEndpoint): string => target.endpointId),
          notLoadedSelectedValues = selectedValues.filter(
            (selectedValue: string): boolean => !targetEndpointIds.includes(selectedValue)
          );

        if (notLoadedSelectedValues.length) {
          const selectedOptionsResponse = await this.dataRepository.getEndpoints({
            companyId: this.component.companyId,
            endpointIds: notLoadedSelectedValues,
          });

          targets.push(...(selectedOptionsResponse?.targets || []));
        }
      }

      const filterOptions = this.formatTargetsToFilterOptions(targets, selectedValues);
      this.loadedEndpoints = this.component.filterOptions.length;

      const selectedFilterOptions = filterOptions.filter(object => object.selected);
      const unselectedFilterOptions = filterOptions.filter(object => !object.selected);

      this.component.specialValueSelected =
        selectedFilterOptions.length === 0 || selectedValues.includes(EndpointSpecialValues.ALL);
      this.component.selectedFilterOptions = this.component.specialValueSelected
        ? this.getSpecialSelectedOption()
        : selectedFilterOptions;

      this.reorderEndpoints(selectedFilterOptions, unselectedFilterOptions);

      this.component.enumList.setSelectedOptions(this.component.selectedFilterOptions);
      this.component.enumList.matchCheckboxesToSelectedOptions();
      this.component.enumList.updateSelectAllCheckboxesState();

      setImmediate(() => {
        this.component.setActionButtonDisabledStateWhenLoading(isEmpty(this.component.enumList.getCheckedOptions()));
      });

      this.component.handleLoadingState(false);
    } catch (e) {
      this.component.handleLoadingState(false);
      this.component.isError = true;
      console.error(e);
    }
  }

  public getSpecialSelectedOption(): SelectedOption[] {
    return [
      {
        label: this.component.enumList.allItemsLabel,
        value: EndpointSpecialValues.ALL,
      },
    ];
  }
}
